"use client";

import useDesignsImages from "@/hooks/useDesignsImages";
import Image from "next/image";

interface DesignContainerProps {
  furnishings: boolean;
  roomNames: boolean;
  squareMetersPerRoom: boolean;
  scale: boolean;
  northArrow: boolean;
  entrance: boolean;
  kitchenEquipment: boolean;
  bathroomEquipment: boolean;
  dimensionalChain: boolean;
  currentDesign: string;
  onDesignChange: (name: string) => void;
}

const DesignContainer = ({
  currentDesign,
  onDesignChange,
  ...props
}: DesignContainerProps) => {
  const { designs, designOptions, roomData } = useDesignsImages({
    furnishings: props.furnishings,
    roomNames: props.roomNames,
    squareMetersPerRoom: props.squareMetersPerRoom,
    scale: props.scale,
    northArrow: props.northArrow,
    entrance: props.entrance,
    kitchenEquipment: props.kitchenEquipment,
    bathroomEquipment: props.bathroomEquipment,
    dimensionalChain: props.dimensionalChain,
  });

  return (
    <div className="flex flex-col items-center gap-6 lg:h-full">
      {/* On desktop, this wrapper is the flex-1 area that absorbs remaining height */}
      {/* w-full matters: the column parent is items-center, so without it this
          wrapper shrinks to the image's intrinsic width and the preview never
          uses the 550px the left column allows — the thumbnails below are
          already w-full, which is why they looked wider than the plan (#889). */}
      <div className="w-full lg:flex lg:min-h-0 lg:flex-1 lg:items-center lg:justify-center">
        <div className="gs--design-gallery__preview relative lg:aspect-square lg:max-h-full lg:w-full">
          {designs
            .filter((d) => d.name === currentDesign)
            .map((img, i) => (
              <Image
                key={i}
                src={img.src}
                alt={img.alt}
                width={550}
                height={550}
                loading="lazy"
                className="block h-full w-full object-contain"
              />
            ))}

          {designOptions
            .filter((option) => option.design === currentDesign)
            .filter((option) => option.option)
            .map((img, i) => (
              <Image
                key={i}
                src={img.src}
                alt={img.alt}
                width={500}
                height={500}
                loading="lazy"
                className="absolute top-0 left-0 z-20 h-full w-full object-contain"
              />
            ))}

          {roomData
            .filter((option) => option.option)
            .map((img, i) => (
              <Image
                key={i}
                src={img.src}
                alt={img.alt}
                width={500}
                height={500}
                loading="lazy"
                className="absolute top-0 left-0 z-10 h-full w-full object-contain"
              />
            ))}
        </div>
      </div>

      <div className="gs--design-gallery__thumbnails grid w-full grid-cols-4 gap-3 lg:shrink-0">
        {designs.map((img, index) => (
          <div
            key={index}
            role="button"
            tabIndex={0}
            aria-label={img.alt}
            aria-pressed={img.name === currentDesign}
            onClick={() => onDesignChange(img.name)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onDesignChange(img.name);
              }
            }}
            className={`gs--design-gallery__thumbnail cursor-pointer transition-all duration-200 lg:hover:scale-105 ${
              img.name === currentDesign
                ? "gs--design-gallery__thumbnail--active"
                : ""
            }`}
          >
            <Image
              src={img.src}
              alt={img.alt}
              width={130}
              height={130}
              loading="lazy"
              className="gs--design-gallery__thumbnail-image block h-auto w-full"
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default DesignContainer;
